import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PlusIcon, Edit2, Trash2, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import TaxesService from '@/api/taxes.service';

// BusinessTaxInfo interface
export interface BusinessTaxInfo {
  id: number;
  taxIdentifier: string;
  taxType: any;
  isVerified: boolean;
  verificationDate?: string;
  registrationDate?: string;
  country: string;
  notes?: string;
  taxTypeId: number;
  businessId: number;
}

// Schema for form validation
const businessTaxInfoSchema = z.object({
  taxIdentifier: z.string().min(1, "Tax identifier is required"),
  isVerified: z.boolean().optional(),
  verificationDate: z.string().optional(),
  country: z.string().min(1, "Country is required"),
  notes: z.string().optional(),
  taxTypeId: z.number().optional(),
  businessId: z.number()
});

// Form for adding/editing business tax information
export function BusinessTaxInfoForm({ 
  businessId, 
  taxInfo, 
  onSuccess, 
  onCancel 
}: { 
  businessId: number; 
  taxInfo?: BusinessTaxInfo;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [taxTypes, setTaxTypes] = useState<any[]>([]);
  
  const form = useForm<z.infer<typeof businessTaxInfoSchema>>({
    resolver: zodResolver(businessTaxInfoSchema),
    defaultValues: taxInfo ? {
      ...taxInfo,
      taxTypeId: taxInfo.taxTypeId || undefined
    } : {
      taxIdentifier: '',
      isVerified: false,
      country: '',
      notes: '',
      businessId: businessId
    }
  });

  useEffect(() => {
    const fetchTaxTypes = async () => {
      try {
        const response = await TaxesService.getAllTaxTypes();
        if (response.data.result === 'success') {
          setTaxTypes(response.data.taxTypes);
        }
      } catch (error) {
        console.error('Error fetching tax types:', error);
      }
    };
    
    fetchTaxTypes();
  }, []);

  const onSubmit = async (values: z.infer<typeof businessTaxInfoSchema>) => {
    setLoading(true);
    try {
      let response;
      
      if (taxInfo?.id) {
        // Update existing tax info
        response = await TaxesService.updateBusinessTaxInfo(taxInfo.id, values);
      } else {
        // Create new tax info
        response = await TaxesService.createBusinessTaxInfo(businessId, values);
      }
      
      if (response.data.result === 'success') {
        toast({
          title: taxInfo ? 'Tax Info Updated' : 'Tax Info Added',
          description: response.data.message,
        });
        onSuccess();
      } else {
        toast({
          title: 'Error',
          description: response.data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while saving tax information',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="taxIdentifier"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tax Identifier</FormLabel>
              <FormControl>
                <Input placeholder="Enter tax identifier" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <FormControl>
                <Input placeholder="Enter country" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="isVerified"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={field.onChange}
                  className="h-4 w-4 mt-1"
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Verified</FormLabel>
                <p className="text-sm text-muted-foreground">
                  Check if this tax information has been verified
                </p>
              </div>
            </FormItem>
          )}
        />
        
        {form.watch('isVerified') && (
          <FormField
            control={form.control}
            name="verificationDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Verification Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea placeholder="Additional notes about this tax information" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {taxTypes.length > 0 && (
          <FormField
            control={form.control}
            name="taxTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax Type</FormLabel>
                <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a tax type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {taxTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id.toString()}>
                        {type.name} ({type.percentage}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        <input type="hidden" {...form.register('businessId', { valueAsNumber: true })} />
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {taxInfo ? 'Update' : 'Add'} Tax Info
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

// Component to display and manage business tax information
export function BusinessTaxManagement({ businessId }: { businessId: number }) {
  const [taxInfos, setTaxInfos] = useState<BusinessTaxInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTaxInfo, setEditingTaxInfo] = useState<BusinessTaxInfo | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTaxInfo, setDeletingTaxInfo] = useState<BusinessTaxInfo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchTaxInfos = async () => {
    setLoading(true);
    try {
      const response = await TaxesService.getBusinessTaxInfos(businessId);
      console.log("response.data", response.data);
      if (response.data.result === 'success') {
        setTaxInfos(response.data.businessTaxInfos || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load tax information',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchTaxInfos();
    }
  }, [businessId]);

  const handleAddEdit = (taxInfo?: BusinessTaxInfo) => {
    setEditingTaxInfo(taxInfo);
    setIsDialogOpen(true);
  };

  const handleSuccess = () => {
    setIsDialogOpen(false);
    setEditingTaxInfo(undefined);
    fetchTaxInfos();
  };

  const handleDelete = (taxInfo: BusinessTaxInfo) => {
    setDeletingTaxInfo(taxInfo);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingTaxInfo) return;
    
    setDeleteLoading(true);
    try {
      const response = await TaxesService.deleteBusinessTaxInfo(deletingTaxInfo.id);
      
      if (response.data.result === 'success') {
        toast({
          title: 'Tax Info Deleted',
          description: response.data.message,
        });
        setTaxInfos(taxInfos.filter(item => item.id !== deletingTaxInfo.id));
      } else {
        toast({
          title: 'Error',
          description: response.data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while deleting tax information',
        variant: 'destructive',
      });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDeletingTaxInfo(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Tax Information</h3>
        <Button
          onClick={() => handleAddEdit()}
          size="sm"
          className="gap-1"
        >
          <PlusIcon className="h-4 w-4" />
          Add Tax Info
        </Button>
      </div>
      
      {loading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : taxInfos.length === 0 ? (
        <div className="text-center p-4 border rounded-md bg-muted/50">
          <p className="text-sm text-muted-foreground">No tax information found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {taxInfos.map((taxInfo) => (
            <Card key={taxInfo.id} className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{taxInfo.taxType.name}</span>
                      <Badge className={`text-xs ${taxInfo.isVerified ? 'bg-green-500' : 'bg-red-500'}`}>
                        {taxInfo.isVerified ? 'Verified' : 'Unverified'}
                      </Badge>
                    </div>
                    <p className="text-base">{taxInfo.taxIdentifier}</p>
                    <p className="text-sm text-muted-foreground mt-1">Country: {taxInfo.country}</p>
                    {taxInfo.registrationDate && <p className="text-sm mt-2 italic">Registration Date: {taxInfo.registrationDate}</p>}
                    {taxInfo.verificationDate && <p className="text-sm mt-2 italic">Verification Date: {taxInfo.verificationDate}</p>}
                    {taxInfo.notes && <p className="text-sm mt-2 italic">Notes: {taxInfo.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleAddEdit(taxInfo)}>
                      <Edit2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(taxInfo)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingTaxInfo ? 'Edit' : 'Add'} Tax Information</DialogTitle>
            <DialogDescription>
              {editingTaxInfo ? 'Update the tax information for this business.' : 'Add new tax information for this business.'}
            </DialogDescription>
          </DialogHeader>
          
          <BusinessTaxInfoForm
            businessId={businessId}
            taxInfo={editingTaxInfo}
            onSuccess={handleSuccess}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
      
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this tax information? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 